import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetFlightStatus, type FlightStatusDTO } from '../api';

/**
 * FlightStatusBadge — live status pill for one flight (on time / delayed /
 * cancelled + estimated time + gate/terminal).
 *
 * Loading is on-demand (never polling): autoLoad fires once when the section
 * opens for flights that already carry a flight_no, or the user clicks the
 * small "Status" button. Every failure degrades silently:
 *   - network error            → nothing rendered
 *   - server `available:false` → nothing rendered (auto), or a discreet muted
 *     note when the user explicitly asked.
 */
interface Props {
  flightNo: string;
  date?: string;
  autoLoad?: boolean;
}

function timeOnly(v?: string): string | undefined {
  if (!v) return undefined;
  const m = String(v).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : v;
}

// Provider status strings (scheduled|active|landed|delayed|cancelled…) → tone.
type Tone = 'good' | 'warn' | 'bad';
function toneOf(status: string | undefined): Tone | null {
  const s = (status || '').toLowerCase();
  if (!s) return null;
  if (s.includes('cancel')) return 'bad';
  if (s.includes('delay')) return 'warn';
  return 'good'; // scheduled / active / landed / on time → neutral-good
}

// LOT U1b — un badge de statut VOL n'est pas une donnée utilisateur : c'est de
// l'interface, donc il porte les jetons sémantiques du thème. L'encre sur le fond
// plein vient de `--on-*` (mesuré), plus d'un `#fff` en dur qui tombait à 2.98:1
// sur le rouge en thème sombre.
const COLORS: Record<Tone, string> = {
  good: 'var(--success)', warn: 'var(--warning)', bad: 'var(--danger)',
};
const INKS: Record<Tone, string> = {
  good: 'var(--on-success)', warn: 'var(--on-warning)', bad: 'var(--on-danger)',
};

export default function FlightStatusBadge({ flightNo, date, autoLoad = false }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<FlightStatusDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [asked, setAsked] = useState(false); // user explicitly clicked Status
  const inFlight = useRef(false);

  const load = async (byUser: boolean) => {
    if (!flightNo || inFlight.current) return;
    inFlight.current = true;
    if (byUser) setAsked(true);
    setLoading(true);
    try {
      setData(await apiGetFlightStatus(flightNo, date || ''));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
      setLoaded(true);
      inFlight.current = false;
    }
  };

  // Auto-load once the flight no is stable (debounced — avoids firing a request
  // on every keystroke while the user edits the field).
  useEffect(() => {
    if (!autoLoad || !flightNo) return;
    const id = setTimeout(() => void load(false), 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, flightNo, date]);

  if (!flightNo) return null;

  // Not fetched yet → offer an on-demand "Status" button.
  if (!loading && !loaded) {
    return (
      <button
        type="button"
        className="btn btn-small"
        style={{ fontSize: 'var(--font-xs)', padding: '1px 8px', flex: '0 0 auto' }}
        onClick={() => void load(true)}
      >
        {t('flights.statusButton')}
      </button>
    );
  }

  if (loading) {
    return <span className="text-dim" style={{ fontSize: 'var(--font-xs)', flex: '0 0 auto' }}>{t('flights.fetching')}</span>;
  }

  // Unavailable (no key / not found) or network error → silent degrade.
  if (!data || data.available === false) {
    if (asked) {
      return (
        <span
          className="text-dim"
          style={{ fontSize: 'var(--font-xs)', flex: '0 0 auto' }}
          title={t('flights.statusUnavailable')}
        >
          {t('flights.statusUnavailable')}
        </span>
      );
    }
    return null;
  }

  const st = data.status || data.status_raw || '';
  const tone = toneOf(st);
  const label =
    tone === 'bad' ? t('flights.cancelled')
    : tone === 'warn' ? t('flights.delayed')
    : st.toLowerCase().includes('land') ? t('flights.landed')
    : t('flights.onTime');
  const color = tone ? COLORS[tone] : 'var(--text-muted)';
  const ink = tone ? INKS[tone] : 'var(--bg)';

  const est = timeOnly(data.estimated_dep) || timeOnly(data.estimated_arr)
    || timeOnly(data.actual_dep) || timeOnly(data.actual_arr)
    || timeOnly(data.scheduled_dep) || timeOnly(data.scheduled_arr);
  const depInfo = [data.gate && `${t('flights.gate')} ${data.gate}`, data.terminal && `${t('flights.terminal')} ${data.terminal}`]
    .filter(Boolean) as string[];

  const emoji = tone === 'bad' ? '🔴' : tone === 'warn' ? '🟠' : '🟢';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: '0 0 auto' }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '1px 9px', borderRadius: 11, fontSize: 'var(--font-xs)', whiteSpace: 'nowrap',
          color: ink, background: color,
        }}
        title={t('flights.status')}
      >
        <span aria-hidden>{emoji}</span>
        {label}
        {est && <span style={{ opacity: 0.9 }}>· {t('flights.estimated')} {est}</span>}
      </span>
      {depInfo.length > 0 && (
        <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>{depInfo.join(' · ')}</span>
      )}
      <button
        type="button"
        className="btn btn-small"
        style={{ fontSize: 'var(--font-xs)', padding: '0 7px', flex: '0 0 auto' }}
        title={t('flights.statusButton')}
        onClick={() => void load(true)}
      >
        ↻
      </button>
    </span>
  );
}
